import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatCentsString } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';

interface CashCutSummaryDialogProps {
  cut: {
    id: string;
    folio_number: string | null;
    expected_cash_cents: string;
    declared_cash_cents: string;
    difference_cents: string;
    credit_card_cents: string;
    debit_card_cents: string;
    abonos_cents: string;
    snapshot_json: string | null;
  };
  on_close: () => void;
}

interface CashCutSnapshot {
  cash_payments_total_cents?: string;
  declared_credit_card_cents?: string;
  declared_debit_card_cents?: string;
  credit_card_difference_cents?: string;
  debit_card_difference_cents?: string;
}

export function CashCutSummaryDialog({ cut, on_close }: CashCutSummaryDialogProps): JSX.Element {
  const { t } = useTranslation();

  const cash_payments_cents = useMemo(() => {
    if (!cut.snapshot_json) return '0';
    try {
      const parsed = JSON.parse(cut.snapshot_json) as CashCutSnapshot;
      return (
        BigInt(parsed.cash_payments_total_cents ?? '0') + BigInt(cut.abonos_cents)
      ).toString();
    } catch {
      return '0';
    }
  }, [cut.abonos_cents, cut.snapshot_json]);
  const snapshot = useMemo(() => {
    if (!cut.snapshot_json) return null;
    try {
      return JSON.parse(cut.snapshot_json) as CashCutSnapshot;
    } catch {
      return null;
    }
  }, [cut.snapshot_json]);
  const declared_credit_card_cents = snapshot?.declared_credit_card_cents ?? '0';
  const declared_debit_card_cents = snapshot?.declared_debit_card_cents ?? '0';
  const credit_difference_cents =
    snapshot?.credit_card_difference_cents ??
    (BigInt(declared_credit_card_cents) - BigInt(cut.credit_card_cents)).toString();
  const debit_difference_cents =
    snapshot?.debit_card_difference_cents ??
    (BigInt(declared_debit_card_cents) - BigInt(cut.debit_card_cents)).toString();

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur"
    >
      <Card className="w-full max-w-xl">
        <CardHeader className="pb-2">
          <CardTitle>{t('cash_shifts.cut_summary_title')}</CardTitle>
          <p className="text-muted-foreground text-xs">
            {t('cash_shifts.cut_summary_folio', {
              folio: cut.folio_number ?? cut.id.slice(0, 8),
            })}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-border overflow-hidden rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">{t('cash_shifts.cut_payment_method_col')}</th>
                  <th className="px-3 py-2 text-right">{t('cash_shifts.cut_expected_col')}</th>
                  <th className="px-3 py-2 text-right">{t('cash_shifts.cut_declared_col')}</th>
                  <th className="px-3 py-2 text-right">{t('cash_shifts.cut_difference_col')}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-border border-t">
                  <td className="px-3 py-2">{t('cash_shifts.method_cash')}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatCentsString(cash_payments_cents)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatCentsString(cut.declared_cash_cents)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatCentsString(cut.difference_cents)}
                  </td>
                </tr>
                <tr className="border-border border-t">
                  <td className="px-3 py-2">{t('cash_shifts.method_debit_card')}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatCentsString(cut.debit_card_cents)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatCentsString(declared_debit_card_cents)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatCentsString(debit_difference_cents)}
                  </td>
                </tr>
                <tr className="border-border border-t">
                  <td className="px-3 py-2">{t('cash_shifts.method_credit_card')}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatCentsString(cut.credit_card_cents)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatCentsString(declared_credit_card_cents)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatCentsString(credit_difference_cents)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="border-border bg-card/40 grid grid-cols-1 gap-2 rounded-md border p-3 text-sm md:grid-cols-3">
            <div>
              <p className="text-muted-foreground text-xs">{t('cash_shifts.cut_expected_cash')}</p>
              <p className="font-mono font-semibold">
                {formatCentsString(cut.expected_cash_cents)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">{t('cash_shifts.cut_declared_cash')}</p>
              <p className="font-mono font-semibold">
                {formatCentsString(cut.declared_cash_cents)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">{t('cash_shifts.cut_difference')}</p>
              <p className="font-mono font-semibold">{formatCentsString(cut.difference_cents)}</p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="button" variant="ghost" onClick={on_close}>
              {t('common.close')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
